import { isServer } from '@utils/server'
import { logger } from './logger'

export const storageKey = {
  authToken: 'AUTH-TOKEN',
  user: 'AUTH-USER',
  lastCheckoutValues: 'LAST-CHECKOUT-VALUES',
  affiliateId: 'AFFILIATE-ID',
  attributionId: 'ATTRIBUTION-ID',
  prospectToken: 'PROSPECT-TOKEN',
  cartItem: 'CART-ITEM',
  cartItemQuantity: 'CART-ITEM-QUANTITY',
} as const

export type StorageKey = (typeof storageKey)[keyof typeof storageKey]

class StorageInterface {
  storageObj: typeof localStorage | typeof sessionStorage

  cache: Record<string, any>

  constructor(storageType: 'local' | 'session') {
    if (isServer()) {
      this.storageObj = {} as Storage
    } else {
      this.storageObj =
        storageType === 'session' ? sessionStorage : localStorage
    }
    this.cache = {}
  }

  private withStorage<T>(procedure: () => T | null | undefined) {
    if (typeof Storage !== 'undefined') {
      try {
        return procedure()
      } catch (err) {
        logger.error('withStorage(): Error', (err as Error).message || err)
        return null
      }
    } else return null
  }

  getByKey<T>(key: StorageKey) {
    return this.withStorage<T>(() => {
      const val = this.cache[key] ?? this.storageObj.getItem(key)
      if (val !== undefined && val !== null) return JSON.parse(val)
      else return val
    })
  }

  setByKey<T>(key: StorageKey, val: T) {
    return this.withStorage<T>(() => {
      const data = JSON.stringify(val)
      this.cache[key] = data
      this.storageObj.setItem(key, data)
      return val
    })
  }

  removeByKey<T>(key: StorageKey) {
    const val = this.getByKey<T>(key)
    return this.withStorage<T>(() => {
      this.cache[key] = undefined
      this.storageObj.removeItem(key)
      return val
    })
  }
}

export const storage = {
  local: new StorageInterface('local'),
  session: new StorageInterface('session'),
}
